import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../components/ui/input-simple';
import { Button } from '../../components/ui/button-simple';
import { Search, MapPin, Users, Plus, Trash2, Eye, Edit3, AlertCircle, ChevronLeft, ChevronRight, Filter, X, User, Building2, Sprout, TrendingUp, BookOpen, Award, MessageSquare, ClipboardList } from 'lucide-react';
import { municipalities } from '../../data/municipalities';
import { AddFarmerForm } from '../../components/forms/AddFarmerForm';
import { ConfirmDeleteModal } from '../../components/modals/ConfirmDeleteModal';
import { ImportExportButtons } from '../../components/buttons/ImportExportButtons';
import { farmersAPI } from '../../services/api';
import { useFarmers } from '../../hooks/useApi';

// Add animations
const styles = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-in-up { animation: fadeInUp 0.5s ease-out forwards; }
  .animate-fade-in-up:nth-child(1) { animation-delay: 0.1s; }
  .animate-fade-in-up:nth-child(2) { animation-delay: 0.2s; }
  .animate-fade-in-up:nth-child(3) { animation-delay: 0.3s; }
  .animate-fade-in-up:nth-child(4) { animation-delay: 0.4s; }
  .animate-fade-in-up:nth-child(5) { animation-delay: 0.5s; }
  .animate-fade-in-up:nth-child(6) { animation-delay: 0.6s; }

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-slide-down { animation: slideDown 0.6s ease-out forwards; }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(30px) scale(0.95); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  .animate-slide-up { animation: slideUp 0.4s ease-out forwards; }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  if (!document.head.querySelector('style[data-admin-farmers-animations]')) {
    styleSheet.setAttribute('data-admin-farmers-animations', 'true');
    document.head.appendChild(styleSheet);
  }
}

export function AdminFarmersDirectory() {
  const navigate = useNavigate();
  const { farmers, loading, error, fetchFarmers, removeFarmer } = useFarmers();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMunicipality, setSelectedMunicipality] = useState('all');
  const [selectedGender, setSelectedGender] = useState('all');
  const [selectedOwnership, setSelectedOwnership] = useState('all');
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editFarmer, setEditFarmer] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, farmer: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    const loadData = async () => {
      try { await fetchFarmers(() => farmersAPI.getAll()); } catch (error) {}
    };
    loadData();
  }, [fetchFarmers]);

  const filteredFarmers = useMemo(() => {
    return farmers.filter(farmer => {
      if (farmer.id === null || farmer.id === undefined) return false;
      const matchesSearch = (farmer.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (farmer.barangay || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesMunicipality = selectedMunicipality === 'all' || farmer.municipality === selectedMunicipality;
      const matchesGender = selectedGender === 'all' || (farmer.gender || '').trim().toLowerCase() === selectedGender.toLowerCase();
      const matchesOwnership = selectedOwnership === 'all' || (farmer.landOwnership || '').toLowerCase().includes(selectedOwnership.toLowerCase());
      return matchesSearch && matchesMunicipality && matchesGender && matchesOwnership;
    });
  }, [farmers, searchTerm, selectedMunicipality, selectedGender, selectedOwnership]);

  const paginatedFarmers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredFarmers.slice(startIndex, endIndex);
  }, [filteredFarmers, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredFarmers.length / itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, selectedMunicipality, selectedGender, selectedOwnership]);

  const handleAddFarmer = async (newFarmer) => {
    try {
      const result = await farmersAPI.create(newFarmer);
      if (result.success) {
        await fetchFarmers(() => farmersAPI.getAll());
      } else {
        alert('Failed to add farmer: ' + (result.error || result.message || 'Unknown error'));
      }
    } catch (error) {
      alert('Failed to add farmer: ' + error.message);
    }
  };

  const handleEditFarmer = async (updatedData, profileId) => {
    try {
      const result = await farmersAPI.update(profileId, updatedData);
      if (result.success) {
        await fetchFarmers(() => farmersAPI.getAll());
        setEditFarmer(null);
      } else {
        alert('Failed to update farmer: ' + (result.error || result.message || 'Unknown error'));
      }
    } catch (error) {
      alert('Failed to update farmer: ' + error.message);
    }
  };

  const handleBulkImport = async (farmersToImport) => {
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (let i = 0; i < farmersToImport.length; i++) {
      const farmerData = farmersToImport[i];
      try {
        const response = await farmersAPI.create(farmerData);
        if (response.success) {
          successCount++;
        } else {
          failCount++;
          errors.push(`Row ${i + 1}: ${response.message || 'Unknown error'}`);
        }
      } catch (error) {
        failCount++;
        const errorMsg = error.message || error.response?.data?.error || 'Unknown error';
        errors.push(`Row ${i + 1}: ${errorMsg}`);
      }
    }
    await fetchFarmers(() => farmersAPI.getAll());
    return { successCount, failCount, errors };
  };

  const handleDeleteClick = (farmer) => {
    setDeleteModal({ isOpen: true, farmer });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.farmer) return;
    setIsDeleting(true);
    try {
      try { await farmersAPI.delete(deleteModal.farmer.id); } catch (apiError) {}
      removeFarmer(deleteModal.farmer.id);
      if (selectedFarmer && selectedFarmer.id === deleteModal.farmer.id) {
        setSelectedFarmer(null);
      }
      setDeleteModal({ isOpen: false, farmer: null });
    } catch (error) {
      alert('Failed to delete farmer. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, farmer: null });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 bg-gradient-to-r from-amber-600 to-amber-700 rounded-2xl shadow-xl p-5 text-white animate-slide-down relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                <Users className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold mb-1">Cacao Farmers Directory (Admin)</h1>
                <p className="text-amber-50 max-w-2xl text-sm">
                  Comprehensive profiles of registered cacao farmers across Bohol.
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-center">
              <Button
                onClick={() => navigate('/admin/profile-requests')}
                className="bg-amber-800/80 hover:bg-amber-900 text-white font-semibold flex items-center gap-2 px-4 py-2 rounded-xl shadow border border-amber-400/40 text-xs"
              >
                <ClipboardList className="h-4 w-4 text-amber-200" />
                Pending Requests
              </Button>
              <div className="relative z-50 bg-white/95 rounded-lg p-1">
                <ImportExportButtons 
                  farmers={farmers}
                  onImport={handleBulkImport}
                  userRole="admin"
                />
              </div>
              <Button 
                onClick={() => setShowAddForm(true)}
                className="bg-green-600 text-white hover:bg-green-700 font-semibold flex items-center gap-2 px-6 shadow-lg whitespace-nowrap"
              >
                <Plus className="h-5 w-5" />
                Add Farmer
              </Button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-2xl shadow-lg mb-8 border border-gray-200 relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                placeholder="Search farmers or barangay..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 rounded-xl border-gray-300 focus:border-amber-500 focus:ring-amber-500"
              />
            </div>
            
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
              <select 
                value={selectedMunicipality} 
                onChange={(e) => setSelectedMunicipality(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-gray-300 bg-background px-3 pl-10 py-2 text-sm focus:border-amber-500 focus:ring-amber-500"
              >
                <option value="all">All Municipalities</option>
                {municipalities.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
              <select 
                value={selectedGender} 
                onChange={(e) => setSelectedGender(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-gray-300 bg-background px-3 pl-10 py-2 text-sm focus:border-amber-500 focus:ring-amber-500"
              >
                <option value="all">All Genders</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
              <select 
                value={selectedOwnership} 
                onChange={(e) => setSelectedOwnership(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-gray-300 bg-background px-3 pl-10 py-2 text-sm focus:border-amber-500 focus:ring-amber-500"
              >
                <option value="all">All Ownership Types</option>
                <option value="Owned">Owned</option>
                <option value="Bought">Bought</option>
                <option value="Inherited">Inherited</option>
                <option value="Caretaker">Caretaker</option>
                <option value="Tenanted">Tenanted</option>
              </select>
            </div>
          </div>
          
          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 bg-amber-500 rounded-full"></div>
              <p className="text-sm text-gray-600 font-medium">
                Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredFarmers.length)} of {filteredFarmers.length} farmers
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm('');
                setSelectedMunicipality('all');
                setSelectedGender('all');
                setSelectedOwnership('all');
              }}
              className="flex items-center gap-2 rounded-xl hover:bg-gray-50"
            >
              <X className="h-4 w-4" />
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mr-2"></div>
            <span className="text-gray-600">Loading farmers...</span>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-800 font-medium">Error loading farmers</span>
            </div>
            <p className="text-red-600 mt-2">{error}</p>
            <Button onClick={() => window.location.reload()} className="mt-4 bg-red-600 hover:bg-red-700">
              Retry
            </Button>
          </div>
        )}

        {/* Farmers Grid */}
        {!loading && !error && filteredFarmers.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedFarmers.map((farmer) => (
                <div key={farmer.id} className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-amber-300 overflow-hidden flex flex-col animate-fade-in-up" style={{ height: '380px' }}>
                  <div className="bg-gradient-to-r from-amber-50 to-amber-100 p-2.5 border-b border-amber-200 flex-shrink-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg text-amber-900 font-bold mb-1 group-hover:text-amber-700 transition-colors line-clamp-1">
                          {farmer.name}
                        </h3>
                        <div className="flex items-center mt-1 text-gray-700">
                          <MapPin className="h-4 w-4 mr-1 flex-shrink-0 text-amber-600" />
                          <span className="text-sm">{farmer.address}</span>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${
                        (farmer.gender || '').trim().toLowerCase() === 'male' ? 'bg-amber-500 text-white' : 'bg-blue-500 text-white'
                      }`}>
                        {farmer.gender}
                      </span>
                    </div>
                  </div>

                  <div className="p-2.5 flex-1 flex flex-col overflow-y-auto">
                    <div className="space-y-1.5 flex-1">
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-amber-600" />
                          <span className="text-sm text-gray-600 font-medium">Age</span>
                        </div>
                        <span className="font-semibold text-gray-900">{farmer.age} years</span>
                      </div>

                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Sprout className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-gray-600 font-medium">Farm Size</span>
                        </div>
                        <span className="font-semibold text-gray-900">{farmer.farmSize} ha</span>
                      </div>

                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-blue-600" />
                          <span className="text-sm text-gray-600 font-medium">Ownership</span>
                        </div>
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          {farmer.landOwnership}
                        </span>
                      </div>

                      <div className="flex items-start justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Award className="h-4 w-4 text-amber-600" />
                          <span className="text-sm text-gray-600 font-medium">Varieties</span>
                        </div>
                        <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">
                          {(farmer.cacaoVarieties || []).map((variety, index) => (
                            <span key={index} className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                              {variety}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-1.5 mt-1.5 flex-shrink-0">
                      <Button 
                        className="flex-1 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-semibold flex items-center justify-center gap-1.5 rounded-xl shadow-md hover:shadow-lg transition-all py-2 h-9 text-xs"
                        onClick={() => setSelectedFarmer(farmer)}
                      >
                        <Eye className="h-4 w-4" />
                        Profile
                      </Button>
                      <Button 
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center justify-center gap-1 rounded-xl shadow-md hover:shadow-lg transition-all px-2.5 h-9 text-xs"
                        onClick={() => navigate(`/messages?partnerId=farmer_${farmer.id}&partnerName=${encodeURIComponent(farmer.name)}&partnerRole=farmer`)}
                        title="Message Farmer"
                      >
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline"
                        className="px-2.5 text-blue-600 border-2 border-blue-300 hover:bg-blue-50 hover:border-blue-500 rounded-xl transition-all h-9"
                        onClick={() => setEditFarmer(farmer)}
                        title="Edit Farmer"
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline"
                        className="px-2.5 text-red-600 border-2 border-red-300 hover:bg-red-50 hover:border-red-500 rounded-xl transition-all h-9"
                        onClick={() => handleDeleteClick(farmer)}
                        title="Delete Farmer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8 bg-white p-6 rounded-2xl shadow-lg">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-2 px-6 rounded-xl hover:bg-amber-50 hover:border-amber-300 disabled:opacity-50"
                >
                  <ChevronLeft className="h-5 w-5" />
                  Previous
                </Button>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Page</span>
                  <span className="px-4 py-2 bg-gradient-to-r from-amber-600 to-amber-700 text-white font-bold rounded-xl shadow-md">
                    {currentPage}
                  </span>
                  <span className="text-sm text-gray-600">of {totalPages}</span>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-2 px-6 rounded-xl hover:bg-amber-50 hover:border-amber-300 disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            )}
          </>
        )}

        {/* Farmer Detail Modal */}
        {selectedFarmer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
              <div className="p-6">
                <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-6 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                        <User className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">Farmer Profile</h2>
                        <p className="text-amber-50 text-sm">Comprehensive farmer information</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        onClick={() => { setSelectedFarmer(null); setEditFarmer(selectedFarmer); }}
                        className="text-white hover:bg-white/20 rounded-xl h-12 w-12 p-0 transition-all hover:scale-110"
                      >
                        <Edit3 className="h-6 w-6" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        onClick={() => setSelectedFarmer(null)}
                        className="text-white hover:bg-white/20 rounded-xl h-12 w-12 p-0 transition-all hover:scale-110"
                      >
                        <X className="h-7 w-7" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-6 p-6 max-h-[70vh] overflow-y-auto">
                  <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-2xl border border-amber-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <User className="h-5 w-5 text-amber-600" />
                      <h3 className="text-lg font-bold text-amber-900">Personal Information</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Name</span>
                        <p className="font-semibold text-gray-900">{selectedFarmer.name}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Age</span>
                        <p className="font-semibold text-gray-900">{selectedFarmer.age} years</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Gender</span>
                        <p className="font-semibold text-gray-900">{selectedFarmer.gender || 'N/A'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Civil Status</span>
                        <p className="font-semibold text-gray-900">{selectedFarmer.civilStatus || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-2xl border border-blue-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <MapPin className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-bold text-blue-900">Location</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Municipality</span>
                        <p className="font-semibold text-gray-900">{selectedFarmer.municipality || 'N/A'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Barangay</span>
                        <p className="font-semibold text-gray-900">{selectedFarmer.barangay || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Farmer Modal */}
        {showAddForm && (
          <AddFarmerForm 
            onClose={() => setShowAddForm(false)}
            onSave={handleAddFarmer}
          />
        )}

        {/* Edit Farmer Modal */}
        {editFarmer && (
          <AddFarmerForm 
            farmerData={editFarmer}
            isEditing={true}
            onClose={() => setEditFarmer(null)}
            onSave={(updatedData) => handleEditFarmer(updatedData, editFarmer.id)}
          />
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmDeleteModal
          isOpen={deleteModal.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Farmer Profile"
          message={`Are you sure you want to delete ${deleteModal.farmer?.name}? This action cannot be undone.`}
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
}
